import { Link } from 'gatsby';
import React from 'react';
import { ArrowRight } from 'lucide-react';
import styled from '../../styled';
import { COLORS } from '../../themes/color';
import { sendCtaClickEvent } from '../../tracking';
import { DemoButton } from '../DemoButton';
import { Headline } from '../Headline';
import { Reveal } from '../motion';

const SectionWrapper = styled('section')`
  position: relative;
  background: #fff;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image: radial-gradient(
      rgba(14, 28, 43, 0.06) 1px,
      transparent 1px
    );
    background-size: 24px 24px;
    mask-image: radial-gradient(
      ellipse 70% 60% at 50% 40%,
      black 30%,
      transparent 100%
    );
    pointer-events: none;
  }
`;

const Inner = styled('div')`
  position: relative;
  max-width: 1000px;
  margin: 0 auto;
  padding: 96px 24px 88px;
  text-align: center;

  @media (max-width: 960px) {
    padding: 64px 24px 56px;
  }
`;

const SectionDescription = styled('p')`
  font-size: 18px;
  line-height: 1.6;
  color: #4f4f4f;
  max-width: 660px;
  margin: 16px auto 48px;
`;

const CardGrid = styled('div')`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  text-align: left;

  @media (max-width: 800px) {
    grid-template-columns: 1fr;
  }

  > * {
    height: 100%;
    min-width: 0;
  }
`;

const Card = styled('div')`
  height: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  padding: 32px 28px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(14, 28, 43, 0.04);
  transition: border-color 0.25s ease, box-shadow 0.25s ease;

  &:hover {
    border-color: ${COLORS.PRIMARY};
    box-shadow: 0 8px 30px rgba(24, 144, 255, 0.12);
  }
`;

const CardTitle = styled('h3')`
  font-size: 20px;
  font-weight: 700;
  color: ${COLORS.INK};
  margin: 0;
`;

const CardBody = styled('p')`
  font-size: 15px;
  line-height: 1.65;
  color: #64748b;
  margin: 0 0 12px;
  flex: 1;
`;

const SecondaryLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 48px;
  color: ${COLORS.PRIMARY};
  font-weight: 600;
  font-size: 16px;
  text-decoration: none;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.8;
  }
`;

export const PreFooterCta = () => {
  return (
    <SectionWrapper>
      <Inner>
        <Reveal>
          <Headline component="h2">Earn what your content is worth</Headline>
          <SectionDescription>
            Sponsorship campaigns and commerce yield run on the same
            foundation: your pages, links, commissions, and product data in
            one place.
          </SectionDescription>
        </Reveal>
        <CardGrid>
          <Reveal>
            <Card>
              <CardTitle>For publishers: sell campaigns and grow commerce yield</CardTitle>
              <CardBody>
                Price and pitch your inventory with evidence, catch revenue
                leaks early, and ask the Agent questions on your own links,
                pages, and campaigns. We&apos;ll walk through it on data like
                yours.
              </CardBody>
              <DemoButton text="Get a demo" variant="contained" />
            </Card>
          </Reveal>
          <Reveal delay={100}>
            <Card>
              <CardTitle>
                For brands, agencies &amp; networks: fund campaigns with premium
                publishers
              </CardTitle>
              <CardBody>
                Import your product catalog to find publishers already
                reaching your customers, then fund sponsorship campaigns
                across their portfolios.
              </CardBody>
              <SecondaryLink
                to="/demand/"
                onClick={() => {
                  sendCtaClickEvent({ label: 'prefooter' });
                }}
              >
                Explore demand solutions <ArrowRight size={18} />
              </SecondaryLink>
            </Card>
          </Reveal>
        </CardGrid>
      </Inner>
    </SectionWrapper>
  );
};
